import React, { useMemo, useState } from "react";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { v4 as uuid } from "uuid";

import { useToast } from "../../hooks/use-toast";

import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import Input from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Textarea } from "../ui/textarea";
import { Toaster } from "../ui/toaster";
import Footer from "../templates/Footer";
import Header from "../templates/Header";
import { useCreateInquiry } from "../../queries/inquiry.queries";

type ContactFormState = {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

type ContactFormErrors = Partial<Record<keyof ContactFormState, string>>;

const DEFAULT_FORM: ContactFormState = {
  fullName: "",
  email: "",
  phone: "",
  subject: "General inquiry",
  message: "",
};

function digitsOnly(value: string) {
  return value.replace(/[^\d]/g, "");
}

function isValidEmail(value: string) {
  return /^\S+@\S+\.\S+$/.test(value);
}

const ContactUs: React.FC = () => {
  const { toast } = useToast();
  const createInquiry = useCreateInquiry();
  const [form, setForm] = useState<ContactFormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contact = useMemo(() => {
    const phoneDisplay = "+(60) 12-350 4244";
    const phoneDigits = "60123504244";
    const email = "info@carbaseauto.com";
    const locations = [
      {
        name: "JVM CarMotors Kg. Jawa",
        mapsQuery: "JVM CarMotors Kg. Jawa Bukit Naga",
        mapsHref:
          "https://www.google.com/maps?q=JVM+CarMotors+Kg.+Jawa+Bukit+Naga",
      },
      {
        name: "J.V. Car Motors Enterprise",
        mapsQuery: "J.V. Car Motors Enterprise",
        mapsHref: "https://www.google.com/maps?q=J.V.+Car+Motors+Enterprise",
      },
    ];

    return {
      phoneDisplay,
      phoneHref: `tel:+${phoneDigits}`,
      whatsappHref: `https://wa.me/${phoneDigits}`,
      email,
      emailHref: `mailto:${email}`,
      hours: [
        { label: "Mon – Sat", value: "9:00 AM – 7:00 PM" },
        { label: "Sunday", value: "10:00 AM – 5:00 PM" },
      ],
      locations,
    };
  }, []);

  const validate = (next: ContactFormState): ContactFormErrors => {
    const nextErrors: ContactFormErrors = {};

    if (!next.fullName.trim()) nextErrors.fullName = "Full name is required.";
    if (!next.email.trim()) nextErrors.email = "Email is required.";
    else if (!isValidEmail(next.email.trim()))
      nextErrors.email = "Please enter a valid email address.";

    if (!next.phone.trim()) nextErrors.phone = "Phone number is required.";
    else if (digitsOnly(next.phone).length < 8)
      nextErrors.phone = "Please enter a valid phone number.";

    if (!next.message.trim()) nextErrors.message = "Message is required.";
    else if (next.message.trim().length < 10)
      nextErrors.message = "Message should be at least 10 characters.";

    return nextErrors;
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast({
        title: "Please check the form",
        description: "Some fields need attention before you can submit.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const inquiryPayload = {
        id: uuid(),
        email: form.email,
        full_name: form.fullName,
        phone_number: form.phone,
        subject: form.subject,
        message: form.message,
      };

      await createInquiry.mutateAsync(inquiryPayload);

      setIsSubmitted(true);
      toast({
        title: "Message ready",
        description:
          "Thanks for reaching out. You can email or WhatsApp us and we’ll respond soon.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const mailtoHref = useMemo(() => {
    const subject = encodeURIComponent(form.subject || "Contact request");
    const body = encodeURIComponent(
      [
        `Name: ${form.fullName}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        "",
        form.message,
      ].join("\n"),
    );

    return `mailto:${contact.email}?subject=${subject}&body=${body}`;
  }, [contact.email, form]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <section className="relative bg-gradient-to-br from-slate-900 via-orange-400 to-slate-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-lg sm:text-xl mb-8 text-blue-100">
            Questions about a vehicle, financing, or availability? Send us a
            message and we’ll get back to you.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-6">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild variant="secondary" className="bg-white/90">
                <a href={contact.phoneHref}>
                  <Phone className="mr-2 h-4 w-4" />
                  Call us
                </a>
              </Button>
              <Button asChild variant="secondary" className="bg-white/90">
                <a href={contact.emailHref}>
                  <Mail className="mr-2 h-4 w-4" />
                  Email
                </a>
              </Button>
              <Button asChild variant="secondary" className="bg-white/90">
                <a href={contact.whatsappHref} target="_blank" rel="noreferrer">
                  <Send className="mr-2 h-4 w-4" />
                  WhatsApp
                </a>
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Contact details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-slate-700 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">Phone</p>
                    <a
                      href={contact.phoneHref}
                      className="text-slate-700 hover:text-slate-900 underline underline-offset-4"
                    >
                      {contact.phoneDisplay}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-slate-700 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">Email</p>
                    <a
                      href={contact.emailHref}
                      className="text-slate-700 hover:text-slate-900 underline underline-offset-4 break-all"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-slate-700 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">Business hours</p>
                    <div className="mt-1 space-y-1 text-slate-700">
                      {contact.hours.map((h) => (
                        <div
                          key={h.label}
                          className="flex justify-between gap-4"
                        >
                          <span className="text-sm">{h.label}</span>
                          <span className="text-sm font-medium">{h.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Locations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contact.locations.map((loc) => (
                  <div key={loc.name} className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-slate-700 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{loc.name}</p>
                      <a
                        href={loc.mapsHref}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-700 hover:text-slate-900 underline underline-offset-4"
                      >
                        Open in Google Maps
                      </a>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-7">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-xl">Send a message</CardTitle>
              </CardHeader>
              <CardContent>
                {!isSubmitted ? (
                  <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="fullName">Full name *</Label>
                        <Input
                          id="fullName"
                          value={form.fullName}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, fullName: e.target.value }))
                          }
                          placeholder="Your name"
                        />
                        {errors.fullName && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors.fullName}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          value={form.phone}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, phone: e.target.value }))
                          }
                          placeholder="+(60) 12-345 6789"
                        />
                        {errors.phone && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, email: e.target.value }))
                        }
                        placeholder="you@example.com"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="subject">Subject*</Label>
                      <Input
                        id="subject"
                        value={form.subject}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, subject: e.target.value }))
                        }
                        placeholder="What can we help with?"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={form.message}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, message: e.target.value }))
                        }
                        placeholder="Tell us what you’re looking for..."
                        rows={6}
                      />
                      {errors.message && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button type="submit" disabled={isSubmitting}>
                        <Send className="mr-2 h-4 w-4" />
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setForm(DEFAULT_FORM);
                          setErrors({});
                        }}
                        disabled={isSubmitting}
                      >
                        Clear
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600">
                      By submitting, you agree we may contact you via phone or
                      email about your request.
                    </p>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-lg border bg-white p-4">
                      <p className="font-semibold text-slate-900">
                        Thanks — we’ve got your message.
                      </p>
                      <p className="text-slate-700 mt-1">
                        For fastest response, you can email or WhatsApp us using
                        the buttons below.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button asChild>
                        <a href={mailtoHref}>
                          <Mail className="mr-2 h-4 w-4" />
                          Email this message
                        </a>
                      </Button>
                      <Button asChild variant="outline">
                        <a
                          href={contact.whatsappHref}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          WhatsApp
                        </a>
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setIsSubmitted(false);
                          setForm(DEFAULT_FORM);
                          setErrors({});
                        }}
                      >
                        Send another message
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {contact.locations.map((loc) => (
                <Card key={`${loc.name}-map`} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{loc.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg overflow-hidden border bg-white">
                      <iframe
                        title={`${loc.name} map`}
                        src={`https://www.google.com/maps?q=${encodeURIComponent(
                          loc.mapsQuery,
                        )}&output=embed`}
                        className="w-full h-64"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                    <div className="mt-3">
                      <Button asChild variant="outline" className="w-full">
                        <a href={loc.mapsHref} target="_blank" rel="noreferrer">
                          <MapPin className="mr-2 h-4 w-4" />
                          Open directions
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ContactUs;
